import {
  Authenticator,
  useAuthenticator,
  View,
  Image,
  components,
} from '@aws-amplify/ui-react';
import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ShopifyConfigContext } from 'components/ShopifyProvider';

import {
  USER_POOL_DOMAIN,
  USER_POOL_CLIENT_ID,
  USER_POOL_SIGN_IN_URL,
  MEETING_LINK,
  TERMS_LINK,
  POLICY_LINK,
} from 'utils/configs';
import { generateCodeVerifier, generateCodeChallenge } from 'utils/pkce';
import { exchangeCodeForTokens, saveCognitoTokens } from 'utils/authTokens';
import { setShopifyParams, getShopifyParams } from 'utils/shopifyStorage';
import { AUTH_META } from 'utils/constants';
import GoogleSignInButton from 'pages/auth/GoogleSignInButton';
import Modal from 'components/common/modals/Modal';
import WelcomeVideo from 'components/WelcomeVideo';
import logo from 'media/images/logo_orgn.svg';

const SignInUp = () => {
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const { isShopifyEmbedded } = useContext(ShopifyConfigContext);
  const popupRef = useRef(null);
  const { user } = useAuthenticator(context => [context.user]);
  const [searchParams] = useSearchParams();
  const initialState = searchParams.get('state') || 'signIn';

  //** Fallback via sessionStorage */
  useEffect(() => {
    if (!isShopifyEmbedded) return;

    const checkSessionStorage = setInterval(async () => {
      const authResult = sessionStorage.getItem('authResult');
      if (authResult) {
        sessionStorage.removeItem('authResult');
        clearInterval(checkSessionStorage);

        try {
          const data = JSON.parse(authResult);
          if (data.type === 'auth-success') {
            await processAuthSuccess(data);
          }
        } catch (err) {
          console.error('Failed to parse authResult:', err);
        }
      }
    }, 500);

    return () => clearInterval(checkSessionStorage);
  }, [isShopifyEmbedded]);

  const processAuthSuccess = async (data = {}) => {
    try {
      const { fullUrl, host, shop } = data;

      setShopifyParams(shop, host);

      console.log('Processing auth:', { host, shop });

      //** Parsing callback URL */
      const callbackUrl = new URL(fullUrl);
      const urlParams = new URLSearchParams(callbackUrl.search);
      const code = urlParams.get('code');
      const stateParam = urlParams.get('state');

      if (!code) throw new Error('No authorization code');

      //** Get data from state */
      const stateData = JSON.parse(atob(stateParam));
      const { verifier, redirectUri } = stateData;

      console.log('Exchanging code for tokens...');

      //** Tokens exchange */
      const tokens = await exchangeCodeForTokens(
        code,
        redirectUri,
        verifier,
        USER_POOL_DOMAIN,
        USER_POOL_CLIENT_ID
      );

      //** Save tokens */
      saveCognitoTokens(tokens);

      console.log('Authentication complete, redirecting to home...');

      //** Preserving Shopify context when redirecting */
      const redirectParams = new URLSearchParams();
      if (host) redirectParams.set('host', host);
      if (shop) redirectParams.set('shop', shop);

      const redirectUrl = redirectParams.toString()
        ? `/?${redirectParams.toString()}`
        : '/';

      console.log('Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Authentication processing error:', error);
      alert('Failed to complete authentication: ' + error.message);
    }
  };

  const handleAuthCallback = useCallback(async event => {
    if (event.origin !== window.location.origin) {
      console.warn('Message from untrusted origin:', event.origin);
      return;
    }

    console.log('Received message:', event.data);

    if (event.data.type === 'auth-success') {
      console.log('Auth success via postMessage');

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      window.removeEventListener('message', handleAuthCallback);

      //** Passing Shopify parameters */
      await processAuthSuccess(event.data);
    } else if (event.data.type === 'auth-error') {
      console.error('Auth error:', event.data.error);
      window.removeEventListener('message', handleAuthCallback);
      alert('Authorization error: ' + event.data.error);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    //** Generating PKCE */
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    console.log('Generated PKCE code_challenge:', codeChallenge);

    if (isShopifyEmbedded) {
      //** EMBEDDED MODE */
      const params = new URLSearchParams(window.location.search);
      const storedParams = getShopifyParams();

      const host = params.get('host') || storedParams?.host;
      const shop = params.get('shop') || storedParams?.shop;

      const redirectUri = `${USER_POOL_SIGN_IN_URL}/auth/callback`;

      const embeddedStateData = {
        timestamp: Date.now(),
        verifier: codeVerifier,
        origin: window.location.origin,
        redirectUri,
        host,
        shop,
      };
      const embeddedStateParam = btoa(JSON.stringify(embeddedStateData));

      console.log('Embedded mode - Redirect URI:', redirectUri);

      const cognitoOAuthUrl =
        `https://${USER_POOL_DOMAIN}/oauth2/authorize?` +
        `client_id=${USER_POOL_CLIENT_ID}&` +
        `response_type=code&` +
        `scope=email+openid+profile&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `identity_provider=Google&` +
        `state=${embeddedStateParam}&` +
        `prompt=select_account&` +
        `code_challenge=${codeChallenge}&` +
        `code_challenge_method=S256`;

      const width = 500;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      popupRef.current = window.open(
        cognitoOAuthUrl,
        `GoogleSignIn_${Date.now()}`,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      if (!popupRef.current) {
        alert('Popup blocked! Please allow popups for this site.');
        return;
      }

      window.addEventListener('message', handleAuthCallback);

      const checkPopup = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', handleAuthCallback);
        }
      }, 1000);
    } else {
      //** STANDALONE MODE */
      const redirectUri = `${USER_POOL_SIGN_IN_URL}/callback`;

      const stateData = {
        verifier: codeVerifier,
        redirectUri,
      };
      const stateParam = btoa(JSON.stringify(stateData));

      const cognitoOAuthUrl =
        `https://${USER_POOL_DOMAIN}/oauth2/authorize?` +
        `client_id=${USER_POOL_CLIENT_ID}&` +
        `response_type=code&` +
        `scope=email+openid+profile&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `identity_provider=Google&` +
        `state=${stateParam}&` +
        `prompt=select_account&` +
        `code_challenge=${codeChallenge}&` +
        `code_challenge_method=S256`;

      console.log('Standalone mode - Redirecting to:', cognitoOAuthUrl);
      window.location.href = cognitoOAuthUrl;
    }
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('message', handleAuthCallback);
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, [handleAuthCallback]);

  if (user) {
    return <Navigate to="/" replace />;
  }

  components = {
    Header() {
      return (
        <View className="auth-logo">
          <Image alt="Mod-y logo" src={logo} height={38} />
        </View>
      );
    },

    Footer() {
      return (
        <View className="auth-welcome-info">
          <section className="auth-welcome-text-wrap">
            <h1 className="auth-welcome-title">{AUTH_META.introTitle}</h1>
            <p className="auth-welcome-text">{AUTH_META.introText}</p>
            <p className="auth-welcome-text">{AUTH_META.introText2}</p>
            <div className="auth-welcome-interaction">
              <button
                type="button"
                className="auth-welcome-btn"
                onClick={() => setIsWelcomeModalOpen(true)}
              >
                {AUTH_META.introButtonText}
              </button>
              <a
                href={MEETING_LINK}
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="Company Terms"
                className="auth-welcome-link"
              >
                {AUTH_META.introLinkText}
              </a>
            </div>
          </section>

          {isWelcomeModalOpen && (
            <Modal closeModal={() => setIsWelcomeModalOpen(false)}>
              <WelcomeVideo
                onCloseWelcomeModal={() => setIsWelcomeModalOpen(false)}
              />
            </Modal>
          )}
        </View>
      );
    },

    SignIn: {
      Header() {
        return (
          <GoogleSignInButton
            title={'Sign In with Google'}
            signInFunc={handleGoogleSignIn}
          />
        );
      },
    },

    SignUp: {
      FormFields() {
        return (
          <>
            <GoogleSignInButton
              title={'Sign Up with Google'}
              signInFunc={handleGoogleSignIn}
            />

            <Authenticator.SignUp.FormFields />

            <p className="agree-text">
              * By signing up, you agree to our{' '}
              <a
                href={TERMS_LINK}
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="Company Terms"
                className="agree-link"
              >
                Terms
              </a>{' '}
              of Use and acknowledge you've read our{' '}
              <a
                href={POLICY_LINK}
                target="_blank"
                rel="noopener noreferrer nofollow"
                title="Company Privacy Policy"
                className="agree-link"
              >
                Privacy Policy
              </a>
            </p>
          </>
        );
      },
    },
  };

  return (
    <div>
      <Authenticator
        initialState={initialState}
        socialProviders={[]}
        components={components}
      />
    </div>
  );
};

export default SignInUp;
